import React, { useState, useEffect } from 'react';
import { badgesAPI, providerCompaniesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Award, Edit, Trash2, Send, Star } from 'lucide-react';

export default function BadgesPage() {
  const [badges, setBadges] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: 100,
    category: '',
    image_url: ''
  });
  const [assignData, setAssignData] = useState({
    provider_ids: [],
    message: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [badgesRes, categoriesRes, providersRes] = await Promise.all([
        badgesAPI.getAll(null, false),
        badgesAPI.getCategories(),
        providerCompaniesAPI.getAll()
      ]);
      setBadges(badgesRes.data);
      setCategories(categoriesRes.data.categories);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.category) {
        toast.error('Nombre y categoría son requeridos');
        return;
      }
      await badgesAPI.create(formData);
      toast.success('Insignia creada exitosamente');
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear insignia');
    }
  };

  const handleUpdate = async () => {
    try {
      await badgesAPI.update(editingBadge.id, formData);
      toast.success('Insignia actualizada');
      setEditingBadge(null);
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta insignia?')) return;
    try {
      await badgesAPI.delete(id);
      toast.success('Insignia eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleAssign = async () => {
    try {
      if (assignData.provider_ids.length === 0) {
        toast.error('Selecciona al menos un proveedor');
        return;
      }
      await badgesAPI.assign(selectedBadge.id, assignData.provider_ids, assignData.message);
      toast.success('Insignia asignada exitosamente');
      setShowAssignDialog(false);
      setAssignData({ provider_ids: [], message: '' });
      setSelectedBadge(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', points: 100, category: '', image_url: '' });
  };

  const openEditDialog = (badge) => {
    setEditingBadge(badge);
    setFormData({
      name: badge.name,
      description: badge.description || '',
      points: badge.points,
      category: badge.category,
      image_url: badge.image_url || ''
    });
    setShowCreateDialog(true);
  };

  const openAssignDialog = (badge) => {
    setSelectedBadge(badge);
    setShowAssignDialog(true);
  };

  const toggleProviderSelection = (providerId) => {
    setAssignData(prev => ({
      ...prev,
      provider_ids: prev.provider_ids.includes(providerId)
        ? prev.provider_ids.filter(id => id !== providerId)
        : [...prev.provider_ids, providerId]
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="badges-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Insignias</h1>
          <p className="text-slate-600">Gestiona las insignias del programa de fidelización</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) { setEditingBadge(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-badge-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Insignia
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBadge ? 'Editar Insignia' : 'Crear Nueva Insignia'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Nombre *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Proveedor Estrella"
                  data-testid="badge-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descripción</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción de la insignia"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Categoría *</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="badge-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Puntos</label>
                <Input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                  min={0}
                  data-testid="badge-points-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL de Imagen</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={editingBadge ? handleUpdate : handleCreate} className="flex-1" data-testid="save-badge-btn">
                  {editingBadge ? 'Actualizar' : 'Crear'} Insignia
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {badges.map(badge => (
          <Card key={badge.id} className={!badge.is_active ? 'opacity-50' : ''} data-testid={`badge-card-${badge.id}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    {badge.image_url ? (
                      <img src={badge.image_url} alt={badge.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <Award className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{badge.name}</CardTitle>
                    <Badge variant="outline" className="text-xs mt-1">{badge.category}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(badge)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(badge.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">{badge.description || 'Sin descripción'}</p>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1 text-amber-600 font-semibold">
                  <Star className="w-4 h-4 fill-current" />
                  {badge.points} pts
                </div>
                <Button size="sm" variant="outline" onClick={() => openAssignDialog(badge)} data-testid={`assign-badge-${badge.id}`}>
                  <Send className="w-4 h-4 mr-1" />
                  Asignar
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Código: {badge.badge_code}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {badges.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay insignias creadas</p>
          <p className="text-sm">Crea tu primera insignia para comenzar</p>
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asignar Insignia: {selectedBadge?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Seleccionar Proveedores</label>
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {providers.map(provider => (
                  <label
                    key={provider.id}
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={assignData.provider_ids.includes(provider.id)}
                      onChange={() => toggleProviderSelection(provider.id)}
                      className="rounded"
                    />
                    <span>{provider.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Mensaje (opcional)</label>
              <Input
                value={assignData.message}
                onChange={(e) => setAssignData({ ...assignData, message: e.target.value })}
                placeholder="Felicidades por tu excelente desempeño!"
              />
            </div>
            <Button onClick={handleAssign} className="w-full" data-testid="confirm-assign-btn">
              <Send className="w-4 h-4 mr-2" />
              Asignar a {assignData.provider_ids.length} proveedor(es)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
